<template>
  <div class="chat-panel">
    <!-- Chat Header -->
    <div class="chat-header">
      <h6 class="mb-0">
        <feather-icon icon="MessageSquareIcon" size="16" />
        Lesson Chat
      </h6>
      <small class="text-muted">{{ messages.length }} messages</small>
    </div>

    <!-- Chat Messages -->
    <div class="chat-messages" ref="chatMessages">
      <div v-if="messages.length === 0" class="no-messages">
        <div class="text-center py-4">
          <feather-icon icon="MessageCircleIcon" size="32" class="text-muted" />
          <p class="text-muted mt-2 mb-0">No messages yet</p>
          <small class="text-muted">Start the conversation!</small>
        </div>
      </div>

      <div
        v-for="message in messages"
        :key="message.id"
        class="message"
        :class="{
          'own-message': isOwnMessage(message),
          'teacher-message': message.user.role === 'teacher'
        }"
      >
        <div class="message-content">
          <div class="message-header">
            <span class="message-author" :class="message.user.role">
              {{ message.user.name }}
            </span>
            <span class="message-time">
              {{ formatTime(message.timestamp || message.created_at) }}
            </span>
          </div>
          <div class="message-text">
            {{ message.message }}
          </div>
        </div>
      </div>
    </div>

    <!-- Chat Input -->
    <div class="chat-input">
      <form @submit.prevent="sendMessage" class="d-flex">
        <b-form-input
          v-model="newMessage"
          placeholder="Type your message..."
          :disabled="isSending"
          maxlength="500"
          class="message-input"
          @keydown.enter.exact.prevent="sendMessage"
          @keydown.enter.shift.exact="addLineBreak"
        />
        <b-button
          type="submit"
          variant="primary"
          :disabled="!canSendMessage"
          class="send-button"
        >
          <b-spinner v-if="isSending" small />
          <feather-icon v-else icon="SendIcon" size="16" />
        </b-button>
      </form>

      <!-- Character count -->
      <div class="char-count" v-if="newMessage.length > 400">
        <small class="text-muted">
          {{ newMessage.length }}/500 characters
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ChatPanel',
  props: {
    webinarId: {
      type: [String, Number],
      required: true
    },
    messages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      newMessage: '',
      isSending: false
    }
  },
  computed: {
    canSendMessage() {
      return this.newMessage.trim().length > 0 && !this.isSending && this.currentUser
    },
    currentUser() {
      return this.$store.state.user.userData || {}
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },
      deep: true
    }
  },
  mounted() {
    this.scrollToBottom()
  },
  methods: {
    async sendMessage() {
      if (!this.canSendMessage || !this.currentUser || !this.currentUser.id) return

      const messageText = this.newMessage.trim()
      this.isSending = true

      try {
        // Call the API to send the message
        // This will be implemented when we create the chat controller and API
        const response = await this.$store.dispatch('lessonRoom/sendMessage', {
          webinarId: this.webinarId,
          message: messageText
        })

        // Add message to local list (will also be received via WebSocket)
        const newMessage = {
          id: Date.now(), // Temporary ID, will be replaced by server response
          webinar_id: this.webinarId,
          message: messageText,
          user: {
            id: this.currentUser.id,
            name: this.currentUser.name || 'User',
            role: this.getUserRole()
          },
          timestamp: new Date().toISOString()
        }

        this.$emit('message-sent', newMessage)

        // Clear input
        this.newMessage = ''

      } catch (error) {
        console.error('Failed to send message:', error)
        this.$bvToast.toast('Failed to send message. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isSending = false
      }
    },

    addLineBreak() {
      this.newMessage += '\n'
    },

    isOwnMessage(message) {
      return message && message.user && this.currentUser && message.user.id === this.currentUser.id
    },

    getUserRole() {
      // Determine user role based on inner_role_id
      return this.currentUser.inner_role_id === 2 ? 'teacher' : 'student'
    },

    formatTime(timestamp) {
      if (!timestamp) return ''
      return moment(timestamp).format('HH:mm')
    },

    scrollToBottom() {
      const container = this.$refs.chatMessages
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  overflow: hidden;

  .chat-header {
    padding: 1rem;
    border-bottom: 1px solid #e3e3e3;
    background: #f8f9fa;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h6 {
      color: #2c3e50;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    background: #f8f9fa;

    .no-messages {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .message {
      margin-bottom: 1rem;

      .message-content {
        max-width: 80%;
        background: white;
        padding: 0.75rem;
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        position: relative;

        .message-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.25rem;

          .message-author {
            font-weight: 600;
            font-size: 0.875rem;

            &.teacher {
              color: #007bff;
            }

            &.student {
              color: #28a745;
            }
          }

          .message-time {
            font-size: 0.75rem;
            color: #6c757d;
          }
        }

        .message-text {
          word-wrap: break-word;
          white-space: pre-wrap;
          line-height: 1.4;
        }
      }

      &.own-message {
        display: flex;
        justify-content: flex-end;

        .message-content {
          background: #007bff;
          color: white;

          .message-header .message-time {
            color: rgba(255, 255, 255, 0.8);
          }
        }
      }

      &.teacher-message:not(.own-message) {
        .message-content {
          border-left: 3px solid #007bff;
        }
      }
    }
  }

  .chat-input {
    padding: 1rem;
    border-top: 1px solid #e3e3e3;
    background: white;

    form {
      .message-input {
        border-radius: 20px;
        border: 1px solid #ddd;
        padding: 0.5rem 1rem;
        margin-right: 0.5rem;

        &:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
        }
      }

      .send-button {
        border-radius: 50%;
        width: 40px;
        height: 40px;
        min-width: 40px;
        min-height: 40px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
          flex-shrink: 0;
        }

        &:disabled {
          opacity: 0.6;
        }
      }
    }

    .char-count {
      text-align: right;
      margin-top: 0.25rem;
    }
  }
}

// Custom scrollbar for chat messages
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// Responsive adjustments
@media (max-width: 768px) {
  .chat-panel {
    .message {
      .message-content {
        max-width: 90%;
      }
    }
  }
}
</style>